/* eslint-disable no-restricted-syntax */
import { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  Input,
  Typography,
  Tooltip,
  Form,
} from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { AppState } from '~/store';
import {
  WorkspaceFlagType,
  WorkspaceKeyInitialState,
  WorkspaceKeyPermission,
  WorkspaceKeys,
  workspacePermissionsInitialState,
} from '~/types/WorkspaceKeyTypes';
import { Timestamp } from '~/components';
import { useParams } from '~/hooks';
import { useOrganizationByShortnameQuery } from '~/queries';
import { uiActions, workspaceKeyActions } from '../../../../actions';
import PartyAvatar from '../../../../components/avatars/PartyAvatar';
import { validationConstants } from '../../../../constants';

const PermissionsOptionsWrapper = styled(Box)`
  display: flex;
  padding-top: 20px;

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    max-height: 580px;
  }
`;

const ParentCheckboxWrapper = styled.span`
  label strong {
    margin-bottom: 12px;
    font-weight: bold;
  }
`;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

const WorkspaceKeyActionsDialog: FC<Props> = ({
  dialogOpen,
  createWorkspaceKey,
  currentUser,
  workspaceKeyItem,
  editWorkspaceKey,
  keyId,
  toggleWsKeyDialog,
  prefilledFlags,
}) => {
  const { t } = useTranslation();
  const [state, changeState] = useState<typeof workspacePermissionsInitialState>(
    workspacePermissionsInitialState,
  );
  const [name, setName] = useState(workspaceKeyItem?.name || '');

  const { organizationShortName } = useParams<'organizationShortName'>();
  const { data: currentOrganization } = useOrganizationByShortnameQuery(
    { organizationShortName },
    { enabled: organizationShortName !== undefined },
  );

  useEffect(() => {
    if (!prefilledFlags) {
      return;
    }
    // poor man's deep clone of the existing state
    // lets mutate that a bit and set state
    const newState: typeof workspacePermissionsInitialState = JSON.parse(JSON.stringify(state));
    for (const prefilledKey of prefilledFlags) {
      for (const permissions of Object.values(newState)) {
        if (permissions[prefilledKey] !== undefined) {
          permissions[prefilledKey] = true;
        }
      }
    }
    changeState(newState);

    // Prefill key name
    const permissionLabel =
      WorkspaceKeys.flatMap((key) => key.items).find(({ title }) => title === prefilledFlags[0])
        ?.translationKey || '';
    setName(
      `${currentUser.details.first_name} ${currentUser.details.last_name}${
        permissionLabel.length > 0 ? ', ' : ''
      }${t(permissionLabel)}`,
    );
  }, [prefilledFlags]);

  // If editing, update state with initial permissions and name on mount
  useEffect(() => {
    if (workspaceKeyItem && dialogOpen) {
      const { permissions } = workspaceKeyItem;

      const initialPermissions = WorkspaceKeys.reduce((permsArray, permsKey) => {
        const permissionItems = {} as Record<string, boolean>;

        permsKey.items.forEach((item) => {
          permissionItems[item.title] = permissions.includes(item.title);
        });

        const updatedPerms = { ...permsArray };
        updatedPerms[permsKey.name] = permissionItems;

        return updatedPerms;
      }, {} as WorkspaceKeyInitialState);

      changeState({ ...state, ...initialPermissions });
      setName(workspaceKeyItem.name);
    }
  }, [workspaceKeyItem, dialogOpen]);

  const handleUpdateState = (key: string, workspaceKey: WorkspaceFlagType) => {
    changeState({
      ...state,
      [workspaceKey]: {
        ...state[workspaceKey],
        [key]: !state[workspaceKey][key],
      },
    });
  };

  const isSomeSelected = (workspaceKey: WorkspaceFlagType) =>
    Object.values(state[workspaceKey]).some((i) => i);

  const isAllSelected = (workspaceKey: WorkspaceFlagType) =>
    Object.values(state[workspaceKey]).every((i) => i);

  const hasPermission = (item: WorkspaceKeyPermission) => {
    if (item === 'trigger_processes' && !currentOrganization?.limit_overrides?.processApiEnabled) {
      return false;
    }
    return true;
  };

  const handleUpdateWorkspacePermissions = (workspaceKey: WorkspaceFlagType) => {
    const noKeysSelected = !isSomeSelected(workspaceKey);
    const updatedKeyPermissions = Object.entries(state[workspaceKey]).map(([i]) => [
      i,
      hasPermission(i as WorkspaceKeyPermission) ? noKeysSelected : false,
    ]);

    changeState({
      ...state,
      [workspaceKey]: Object.fromEntries(updatedKeyPermissions),
    });
  };

  const permissionCheckboxes = WorkspaceKeys.map((workspacePermission) => (
    <Box key={workspacePermission.name} mb={24}>
      <ParentCheckboxWrapper>
        <Checkbox
          label={t(`workspaceKeyTypes.${workspacePermission.name}`)}
          checked={isSomeSelected(workspacePermission.name)}
          indeterminate={!isAllSelected(workspacePermission.name)}
          onChange={() => handleUpdateWorkspacePermissions(workspacePermission.name)}
        />
      </ParentCheckboxWrapper>
      <Box ml={[10, 20]}>
        {workspacePermission.items.map((i) => (
          <Box
            display="flex"
            key={i.title}
            justifyContent="space-between"
            alignItems="center"
            mb={6}
          >
            {hasPermission(i.title) ? (
              <Checkbox
                checked={state[workspacePermission.name][i.title]}
                label={i.displayTitle || i.title}
                onChange={() => handleUpdateState(i.title, workspacePermission.name)}
              />
            ) : (
              <Tooltip text={t('triggerProcessesNotAvailable')}>
                <Checkbox label={i.displayTitle || i.title} disabled />
              </Tooltip>
            )}
            <Box width="50%" ml={[4, 16]} color="grey20">
              {t(i.translationKey)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  ));

  const onChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const resetForm = () => {
    toggleWsKeyDialog();
    changeState(workspacePermissionsInitialState);
    setName('');
  };

  const onGenerateSecret = (e: FormEvent) => {
    e.preventDefault();

    const arr: string[] = [];

    // Filter and store selected access keys
    Object.values(state).forEach((item) => {
      Object.entries(item)
        .filter(([, access]) => access)
        .forEach(([key]) => arr.push(key));
    });

    createWorkspaceKey({
      name,
      permissions: arr,
    });

    resetForm();
  };

  const onUpdateSecret = (e: FormEvent) => {
    e.preventDefault();

    const arr: string[] = [];

    // Filter and store selected access keys
    Object.values(state).forEach((item) => {
      Object.entries(item)
        .filter(([, access]) => access)
        .forEach(([key]) => arr.push(key));
    });

    if (keyId) {
      editWorkspaceKey(keyId, {
        name,
        permissions: arr,
      });
    }

    resetForm();
  };

  return (
    <Dialog open={dialogOpen} onClose={resetForm} width={[1, 1, 1, 820]}>
      <Form onSubmit={workspaceKeyItem ? onUpdateSecret : onGenerateSecret}>
        <Dialog.Header>
          <Dialog.Header.Title title={t('workspaceApiAccess')} />
        </Dialog.Header>
        <Dialog.Content>
          <Box pb={20}>
            <Input
              label={t('keyName')}
              value={name}
              onChange={onChangeName}
              placeholder={`${t('enterName')}...`}
              {...validationConstants.name}
            />
          </Box>
          <Box pb={20}>
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              flexDirection={['column', 'row']}
            >
              <Box mb={[8, 0]}>
                <Box display="flex" flexWrap="nowrap" flexDirection="column">
                  <Typography mb={4}>
                    {workspaceKeyItem && workspaceKeyItem.createdBy
                      ? `${t('createdBy')} ${workspaceKeyItem.createdBy.firstName} ${
                          workspaceKeyItem.createdBy.lastName
                        }`
                      : `${t('createdBy')} ${currentUser.details.first_name} ${
                          currentUser.details.last_name
                        }`}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <PartyAvatar
                      size="medium"
                      party={
                        workspaceKeyItem ? { details: workspaceKeyItem.createdBy } : currentUser
                      }
                    />
                    <Typography ml={12} color="grey20">
                      {workspaceKeyItem ? (
                        <Timestamp value={workspaceKeyItem.createTs} year />
                      ) : (
                        t('now')
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box mb={[8, 0]}>
                <Box display="flex" flexWrap="nowrap" flexDirection="column">
                  <Typography mb={4}>{t('uses')}</Typography>
                  <Typography color="grey20">
                    {workspaceKeyItem && workspaceKeyItem.uses ? workspaceKeyItem.uses : '-'}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" flexWrap="nowrap" flexDirection="column">
                <Typography mb={4}>{t('lastUsed')}</Typography>
                <Typography color="grey20">
                  {workspaceKeyItem && workspaceKeyItem.lastUseTs ? (
                    <Timestamp.Diff from={workspaceKeyItem.lastUseTs} />
                  ) : (
                    '-'
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider />
          <PermissionsOptionsWrapper flexWrap="wrap" flexDirection="column">
            {permissionCheckboxes}
          </PermissionsOptionsWrapper>
        </Dialog.Content>
        <Dialog.Actions>
          <Button type="submit" onClick={workspaceKeyItem ? onUpdateSecret : onGenerateSecret}>
            {workspaceKeyItem ? t('update') : t('create')}
          </Button>
          <Button variant="secondary" onClick={resetForm}>
            {t('cancel')}
          </Button>
        </Dialog.Actions>
      </Form>
    </Dialog>
  );
};

const mapStateToProps = (state: AppState) => {
  const { dialogOpen, keyId, prefilledFlags } = state.ui.wsKeyDialogState;
  return {
    dialogOpen,
    keyId,
    prefilledFlags,
    workspaceKeyItem: keyId ? state.workspaceResources.workspaceKeys.byId[keyId] : undefined,
    currentUser: state.userResources.users.current,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      toggleWsKeyDialog: uiActions.toggleWsKeyDialog,
      createWorkspaceKey: workspaceKeyActions.createWorkspaceKey,
      editWorkspaceKey: workspaceKeyActions.editWorkspaceKey,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(WorkspaceKeyActionsDialog);
